import { cn } from "@/lib/utils";
import { Link } from "react-router-dom";
import {
  UserCircle,
  Calendar,
  BookOpen,
  type LucideIcon,
} from "lucide-react";

interface QuickAction {
  id: string;
  label: string;
  description?: string;
  icon: LucideIcon;
  href: string;
  color?: string;
}

interface QuickActionsProps {
  actions?: QuickAction[];
  columns?: 2 | 3 | 4;
  className?: string;
}

const defaultFreelancerActions: QuickAction[] = [
  {
    id: "profile",
    label: "Edit Profile",
    description: "Update your information",
    icon: UserCircle,
    href: "/genius/profile",
    color: "text-blue-600 bg-blue-100",
  },
  {
    id: "courses",
    label: "Continue Learning",
    description: "Resume your training",
    icon: BookOpen,
    href: "/genius/courses",
    color: "text-amber-600 bg-amber-100",
  },
  {
    id: "schedule",
    label: "Availability",
    description: "Manage your schedule",
    icon: Calendar,
    href: "/genius/profile#availability",
    color: "text-emerald-600 bg-emerald-100",
  },
];

export function QuickActions({
  actions = defaultFreelancerActions,
  columns = 3,
  className,
}: QuickActionsProps) {
  const gridCols = {
    2: "grid-cols-2",
    3: "grid-cols-2 lg:grid-cols-3",
    4: "grid-cols-2 lg:grid-cols-3",
  };

  return (
    <div className={cn("grid gap-4", gridCols[columns], className)}>
      {actions.map((action) => (
        <Link key={action.id} to={action.href}>
          <div className="action-card group">
            <div
              className={cn(
                "w-12 h-12 rounded-xl flex items-center justify-center mb-3 transition-transform group-hover:scale-110",
                action.color || "text-[hsl(var(--color-4))] bg-[hsl(var(--color-4))]/10"
              )}
            >
              <action.icon className="h-6 w-6" />
            </div>
            <h3 className="font-semibold text-[hsl(var(--color-7))] group-hover:text-[hsl(var(--color-4))] transition-colors">
              {action.label}
            </h3>
            {action.description && (
              <p className="text-sm text-[hsl(var(--color-8))] mt-1">
                {action.description}
              </p>
            )}
          </div>
        </Link>
      ))}
    </div>
  );
}

// Smaller inline variant
export function QuickActionButton({
  icon: Icon,
  label,
  href,
  className,
}: {
  icon: LucideIcon;
  label: string;
  href: string;
  className?: string;
}) {
  return (
    <Link
      to={href}
      className={cn(
        "inline-flex items-center gap-2 px-4 py-2 rounded-full",
        "bg-[hsl(var(--color-2))] hover:bg-[hsl(var(--color-4))]/10",
        "text-sm font-medium text-[hsl(var(--color-7))]",
        "transition-all duration-200",
        className
      )}
    >
      <Icon className="h-4 w-4" />
      {label}
    </Link>
  );
}
